'use client';
import React from 'react';
import { ListData } from 'react-stately';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Select,
  type SelectItemType,
  MultiSelect,
  AddressAutocompleteField,
  NumberInput,
  DatePicker,
  Label,
  TextArea,
  TimezoneSelect,
  TimeField,
  Toggle,
  HintText,
  Checkbox,
  PopOut
} from '../../..';
import { CalendarDate } from '@internationalized/date';
import type {
  DateValue,
  TimeValue as AriaTimeValue
} from 'react-aria-components';
import { cx } from 'class-variance-authority';
import styles from './EventInformationForm.module.scss';

type FormOption = { id: string; value: string; label: string };
type SponsorOption = { id: string; name: string; logoImageUrl?: string | null };

type TimeValue = {
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

export type EventInformationFormValues = {
  eventTitle?: string;
  projectDate?: string;
  timeZoneId?: string | null;
  locationName?: string;
  eventAddress?: string;
  latitude?: number | null;
  longitude?: number | null;
  eventLocation?: unknown;
  coordinatorFirstName?: string;
  coordinatorLastName?: string;
  coordinatorPhoneNumber?: string;
  coordinatorEmail?: string;
  locationType?: string;
  causeType?: string;
  causeTypeOther?: string;
  activityTypes?: string[];
  activityTypeOther?: string;
  skillTypes?: string[];
  skillTypeOther?: string;
  sponsors?: string[];
  eventDescription?: string;
  doorsAt?: TimeValue | null;
  startTime?: TimeValue | null;
  endTime?: TimeValue | null;
  internalVolunteersCount?: number;
  publicVolunteersCount?: number;
  areMinorsAllowed?: boolean;
  bilingual?: string;
  bilingualOther?: string;
  additionalInformation?: string;
  isPublic?: boolean;
  promoImageUrl?: string;
  isLiabilityAccepted?: boolean;
  externalRegistrationUrl?: string;
};

export type EventInformationFormProps<T extends EventInformationFormValues> = {
  form: UseFormReturn<T>;
  // Data props
  sponsors: Array<SponsorOption>;
  selectedSponsorItems: ListData<SelectItemType>;
  selectedActivityTypeItems: ListData<SelectItemType>;
  selectedSkillTypeItems: ListData<SelectItemType>;
  // Callback props
  onSponsorSearch: (search: string) => void;
  setCoordinatesAndLocation: (place: google.maps.places.PlaceResult) => void;
  isLocaleSwitchOpen: boolean;
  setIsLocaleSwitchOpen: (isLocaleSwitchOpen: boolean) => void;
  setLocale: (locale: string) => void;
  // Options and constants
  bilingualTypeOptions: Array<FormOption>;
  causeTypesOptions: Array<FormOption>;
  locationTypeOptions: Array<FormOption>;
  preferredSkillsOptions: Array<FormOption>;
  typeOfOutreachActivityOptions: Array<FormOption>;
  yesNoOptions: Array<FormOption>;
  MAX_EVENT_DESCRIPTION_LENGTH: number;
  MAX_ADDITIONAL_INFO_LENGTH: number;
  // Translation functions
  t: (key: string) => string;
  // Date utils
  toCalendarDateValue: (
    value: string | null | undefined
  ) => CalendarDate | null;
  toDateString: (value: DateValue | null) => string | null;
  toTimeValue: (value: TimeValue | null | undefined) => AriaTimeValue | null;
  today: (timezone: string) => CalendarDate;
  getLocalTimeZone: () => string;
  // Render props for custom components
  renderBannerGallery?: (field: {
    value: string;
    onChange: (value: string) => void;
  }) => React.ReactNode;
  // Hide fields not applicable in certain contexts (e.g. admin edit mode)
  hideLocaleSwitch?: boolean;
  hidePublicVisibility?: boolean;
  hideLiabilityWaiver?: boolean;
};

export function EventInformationForm<T extends EventInformationFormValues>({
  form: _form,
  sponsors,
  selectedSponsorItems,
  selectedActivityTypeItems,
  selectedSkillTypeItems,
  onSponsorSearch,
  setCoordinatesAndLocation,
  isLocaleSwitchOpen,
  setIsLocaleSwitchOpen,
  setLocale,
  bilingualTypeOptions,
  causeTypesOptions,
  locationTypeOptions,
  preferredSkillsOptions,
  typeOfOutreachActivityOptions,
  yesNoOptions,
  MAX_EVENT_DESCRIPTION_LENGTH,
  MAX_ADDITIONAL_INFO_LENGTH,
  t,
  toCalendarDateValue,
  toDateString,
  toTimeValue,
  today,
  getLocalTimeZone,
  renderBannerGallery,
  hideLocaleSwitch = false,
  hidePublicVisibility = false,
  hideLiabilityWaiver = false
}: EventInformationFormProps<T>) {
  // Cast to the concrete type internally so TypeScript can resolve field names and value types.
  // The external API stays generic so consumers don't need to cast their forms.
  const form = _form as unknown as UseFormReturn<EventInformationFormValues>;
  const customEventAddressError =
    form.formState.errors?.eventAddress ||
    form.formState.errors?.eventLocation ||
    form.formState.errors?.latitude ||
    form.formState.errors?.longitude;

  return (
    <>
      {!hideLocaleSwitch && (
        <div className={styles.toggleContainer}>
          <Toggle
            label={t('seeForm')}
            isSelected={isLocaleSwitchOpen}
            onChange={(value) => {
              setLocale(value ? 'es' : 'en');
              setIsLocaleSwitchOpen(value);
            }}
          />
        </div>
      )}
      <FormField
        control={form.control}
        name="eventTitle"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input
                labelClassName={styles.inputLabel}
                {...field}
                label={t('addPartnerEvent.eventNameLabel')}
                hideRequiredIndicator={false}
                isRequired
                hint={t('addPartnerEvent.eventNameHint')}
                tooltip={t('addPartnerEvent.eventNameTooltip')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className={styles.formRow}>
        <FormField
          control={form.control}
          name="projectDate"
          render={({ field }) => (
            <FormItem className={styles.projectDateFormItem}>
              <FormControl>
                <div className={styles.projectDateContainer}>
                  <Label
                    className={styles.inputLabel}
                    isRequired
                    id="projectDate"
                  >
                    {t('addPartnerEvent.eventDateLabel')}
                  </Label>
                  <DatePicker
                    {...field}
                    className={cx(
                      styles.datePicker,
                      !field.value && styles.placeholder
                    )}
                    aria-labelledby="projectDate"
                    presetButtonLabel={t('today')}
                    fullWidth
                    placeholder={t('addPartnerEvent.eventDatePlaceholder')}
                    onChange={(value) => {
                      field.onChange(toDateString(value) ?? undefined);
                    }}
                    showActions={false}
                    shouldCloseOnSelect
                    value={toCalendarDateValue(field.value)}
                    minValue={today(getLocalTimeZone())}
                  />
                  <HintText>{t('addPartnerEvent.eventDateHint')}</HintText>
                  <FormMessage />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="timeZoneId"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <TimezoneSelect
                  value={field.value ?? undefined}
                  label={t('addPartnerEvent.timezoneLabel')}
                  tooltip={t('addPartnerEvent.timezoneTooltip')}
                  labelClassName={styles.inputLabel}
                  isRequired
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className={styles.formRow}>
        <FormField
          control={form.control}
          name="locationName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  label={t('addPartnerEvent.locationNameLabel')}
                  labelClassName={styles.inputLabel}
                  hideRequiredIndicator={false}
                  isRequired
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="eventAddress"
          render={({ field }) => (
            <FormItem className={styles.formItem}>
              <FormControl>
                <AddressAutocompleteField
                  {...field}
                  value={field.value ?? ''}
                  id="eventAddress"
                  label={t('addPartnerEvent.eventAddressInputLabel')}
                  placeholder={t(
                    'addPartnerEvent.eventAddressInputPlaceholder'
                  )}
                  tooltip={t('addPartnerEvent.eventAddressHint')}
                  labelClassName={styles.inputLabel}
                  isRequired
                  onBlur={field.onBlur}
                  onChange={(value: string) => {
                    form.setValue('latitude', null);
                    form.setValue('longitude', null);
                    field.onChange(value);
                  }}
                  onPlaceSelected={(place) => setCoordinatesAndLocation(place)}
                />
              </FormControl>
              {customEventAddressError?.message && (
                <FormMessage>{customEventAddressError.message}</FormMessage>
              )}
            </FormItem>
          )}
        />
      </div>
      <div>
        <div className={styles.sectionTitleContainer}>
          <h3 className={styles.sectionTitle}>
            {t('addPartnerEvent.coordinatorTitle')}
            <span className={styles.requiredIndicator}>*</span>
          </h3>
          <p className={styles.sectionDescription}>
            {t('addPartnerEvent.coordinatorDescription')}
          </p>
        </div>
        <div className={`${styles.formRow} ${styles.formSectionItem}`}>
          <FormField
            control={form.control}
            name="coordinatorFirstName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={t('addPartnerEvent.coordinatorFirstNameLabel')}
                    labelClassName={styles.inputLabel}
                    hideRequiredIndicator={false}
                    isRequired
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="coordinatorLastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={t('addPartnerEvent.coordinatorLastNameLabel')}
                    labelClassName={styles.inputLabel}
                    hideRequiredIndicator={false}
                    isRequired
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className={`${styles.formRow} ${styles.formSectionItem}`}>
          <FormField
            control={form.control}
            name="coordinatorPhoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    label={t('addPartnerEvent.coordinatorPhoneNumberLabel')}
                    labelClassName={styles.inputLabel}
                    hideRequiredIndicator={false}
                    isRequired
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="coordinatorEmail"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={t('addPartnerEvent.coordinatorEmailLabel')}
                    placeholder="jane@example.com"
                    labelClassName={styles.inputLabel}
                    hideRequiredIndicator={false}
                    isRequired
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <FormField
          control={form.control}
          name="locationType"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value)}
                    label={t('addPartnerEvent.locationTypeLabel')}
                    items={locationTypeOptions.map((option) => ({
                      ...option,
                      label: t(`locationType.${option.label}`)
                    }))}
                    placeholder={t('pleaseSelect')}
                    labelClassName={styles.inputLabel}
                    isRequired
                  >
                    {(item) => (
                      <Select.Item id={item.id}>{item.label}</Select.Item>
                    )}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <div className={styles.causeTypeContainer}>
          <FormField
            control={form.control}
            name="causeType"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Select
                      isRequired
                      value={field.value ?? undefined}
                      label={t('addPartnerEvent.causeTypeLabel')}
                      items={causeTypesOptions.map((option) => ({
                        ...option,
                        label: t(`causeTypes.${option.label}`)
                      }))}
                      placeholder={t('pleaseSelect')}
                      labelClassName={styles.inputLabel}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          {form.watch('causeType') === 'other' && (
            <FormField
              control={form.control}
              name="causeTypeOther"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t('otherSpecify')}
                      labelClassName={styles.inputLabel}
                      hideRequiredIndicator={false}
                      isRequired
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formWithOther}>
          <FormField
            control={form.control}
            name="activityTypes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <MultiSelect
                    {...field}
                    label={t('addPartnerEvent.activityTypeLabel')}
                    showLabelRequired
                    labelClassName={styles.inputLabel}
                    showAvatar={false}
                    items={typeOfOutreachActivityOptions.map((option) => ({
                      id: option.value,
                      label: t(`typeOfOutreach.${option.label}`)
                    }))}
                    selectedItems={selectedActivityTypeItems}
                    onItemInserted={(value) => {
                      const currentActivityTypeIds = field.value || [];
                      field.onChange(
                        currentActivityTypeIds.includes(String(value))
                          ? currentActivityTypeIds
                          : [...currentActivityTypeIds, String(value)]
                      );
                    }}
                    onItemCleared={(value) => {
                      const currentActivityTypeIds = field.value || [];
                      const updatedActivityTypes =
                        currentActivityTypeIds.filter(
                          (activityTypeId: string) => activityTypeId !== value
                        );
                      field.onChange(updatedActivityTypes);
                    }}
                  >
                    {(item) => (
                      <MultiSelect.Item id={item.id}>
                        {item.label}
                      </MultiSelect.Item>
                    )}
                  </MultiSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('activityTypes')?.includes('other') && (
            <FormField
              control={form.control}
              name="activityTypeOther"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t('otherSpecify')}
                      labelClassName={styles.inputLabel}
                      hideRequiredIndicator={false}
                      isRequired
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <div className={styles.formWithOther}>
          <FormField
            control={form.control}
            name="skillTypes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <MultiSelect
                    {...field}
                    label={t('addPartnerEvent.preferredSkillsLabel')}
                    showLabelRequired
                    labelClassName={styles.inputLabel}
                    showAvatar={false}
                    items={preferredSkillsOptions.map((option) => ({
                      id: option.value,
                      label: t(`preferredSkills.${option.label}`)
                    }))}
                    selectedItems={selectedSkillTypeItems}
                    onItemInserted={(value) => {
                      const currentSkillTypeIds = field.value || [];
                      field.onChange(
                        currentSkillTypeIds.includes(String(value))
                          ? currentSkillTypeIds
                          : [...currentSkillTypeIds, String(value)]
                      );
                    }}
                    onItemCleared={(value) => {
                      const currentSkillTypeIds = field.value || [];
                      const updatedSkillTypes = currentSkillTypeIds.filter(
                        (skillTypeId: string) => skillTypeId !== value
                      );
                      field.onChange(updatedSkillTypes);
                    }}
                  >
                    {(item) => (
                      <MultiSelect.Item id={item.id}>
                        {item.label}
                      </MultiSelect.Item>
                    )}
                  </MultiSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('skillTypes')?.includes('other') && (
            <FormField
              control={form.control}
              name="skillTypeOther"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t('otherSpecify')}
                      labelClassName={styles.inputLabel}
                      hideRequiredIndicator={false}
                      isRequired
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
      <FormField
        control={form.control}
        name="sponsors"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <MultiSelect
                {...field}
                label={t('addPartnerEvent.partnerOrganizationLabel')}
                hint={t('addPartnerEvent.partnerOrganizationHint')}
                labelClassName={styles.inputLabel}
                onInputChange={onSponsorSearch}
                items={sponsors.map((sponsor) => ({
                  id: sponsor.id,
                  label: sponsor.name,
                  ...(sponsor.logoImageUrl && {
                    avatarUrl: sponsor.logoImageUrl
                  })
                }))}
                selectedItems={selectedSponsorItems}
                placeholder={t(
                  'addPartnerEvent.partnerOrganizationPlaceholder'
                )}
                onItemInserted={(value) => {
                  const currentSponsorIds = field.value || [];
                  field.onChange(
                    currentSponsorIds.includes(String(value))
                      ? currentSponsorIds
                      : [...currentSponsorIds, String(value)]
                  );
                }}
                onItemCleared={(value) => {
                  const currentSponsorIds = field.value || [];
                  const updatedSponsors = currentSponsorIds.filter(
                    (sponsorId: string) => sponsorId !== value
                  );
                  field.onChange(updatedSponsors);
                }}
              >
                {(item) => (
                  <MultiSelect.Item id={item.id} avatarUrl={item.avatarUrl}>
                    {item.label}
                  </MultiSelect.Item>
                )}
              </MultiSelect>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="eventDescription"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <TextArea
                {...field}
                labelClassName={styles.inputLabel}
                label={t('addPartnerEvent.eventDescriptionLabel')}
                rows={4}
                tooltip={t('addPartnerEvent.eventDescriptionTooltip')}
                isRequired
                hint={`${MAX_EVENT_DESCRIPTION_LENGTH - (field.value?.length || 0)} ${t('charactersLeft')}`}
                maxLength={MAX_EVENT_DESCRIPTION_LENGTH}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className={styles.outreachTimesContainer}>
        <FormField
          control={form.control}
          name="doorsAt"
          render={({ field }) => (
            <FormItem className={styles.outreachTimeField}>
              <FormControl>
                <TimeField
                  value={toTimeValue(field.value)}
                  labelClassName={styles.inputLabel}
                  label={t('addPartnerEvent.doorsAtLabel')}
                  isRequired
                  hourCycle={12}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="startTime"
          render={({ field }) => (
            <FormItem className={styles.outreachTimeField}>
              <FormControl>
                <TimeField
                  value={toTimeValue(field.value)}
                  labelClassName={styles.inputLabel}
                  label={t('addPartnerEvent.startTimeLabel')}
                  isRequired
                  hourCycle={12}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endTime"
          render={({ field }) => (
            <FormItem className={styles.outreachTimeField}>
              <FormControl>
                <TimeField
                  value={toTimeValue(field.value)}
                  labelClassName={styles.inputLabel}
                  label={t('addPartnerEvent.endTimeLabel')}
                  isRequired
                  hourCycle={12}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className={styles.formRow}>
        <FormField
          control={form.control}
          name="internalVolunteersCount"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <NumberInput
                  {...field}
                  label={t('addPartnerEvent.internalVolunteersLabel')}
                  labelClassName={styles.inputLabel}
                  isRequired
                  minValue={0}
                  formatOptions={{ maximumFractionDigits: 0 }}
                  hideRequiredIndicator={false}
                  tooltip={t('addPartnerEvent.internalVolunteersTooltip')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="publicVolunteersCount"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <NumberInput
                  {...field}
                  label={t('addPartnerEvent.publicVolunteersCountLabel')}
                  labelClassName={styles.inputLabel}
                  isRequired
                  minValue={0}
                  formatOptions={{ maximumFractionDigits: 0 }}
                  hideRequiredIndicator={false}
                  tooltip={t('addPartnerEvent.publicVolunteersCountTooltip')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className={styles.formRow}>
        <NumberInput
          value={
            (form.watch('internalVolunteersCount') || 0) +
            (form.watch('publicVolunteersCount') || 0)
          }
          label={t('addPartnerEvent.totalNumberOfVolunteersLabel')}
          tooltip={t('addPartnerEvent.totalNumberOfVolunteersTooltip')}
          labelClassName={styles.inputLabel}
          isRequired
          minValue={0}
          formatOptions={{ maximumFractionDigits: 0 }}
          hideRequiredIndicator={false}
          isDisabled
        />
        <FormField
          control={form.control}
          name="areMinorsAllowed"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Select
                    isRequired
                    value={
                      typeof field.value === 'boolean'
                        ? field.value
                          ? 'yes'
                          : 'no'
                        : ''
                    }
                    label={t('addPartnerEvent.minorsLabel')}
                    items={yesNoOptions.map((option) => ({
                      ...option,
                      label: t(`areMinorsAllowed.${option.label}`)
                    }))}
                    placeholder={t('pleaseSelect')}
                    labelClassName={styles.inputLabel}
                    hint={t('addPartnerEvent.minorsHint')}
                    onChange={(value) => {
                      field.onChange(value === 'yes');
                    }}
                  >
                    {(item) => (
                      <Select.Item id={item.id}>{item.label}</Select.Item>
                    )}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>
      <div className={styles.formWithOther}>
        <FormField
          control={form.control}
          name="bilingual"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Select
                    isRequired
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value)}
                    label={t('addPartnerEvent.bilingualLabel')}
                    items={bilingualTypeOptions.map((option) => ({
                      ...option,
                      label: t(`bilingualType.${option.label}`)
                    }))}
                    placeholder={t('pleaseSelect')}
                    labelClassName={styles.inputLabel}
                  >
                    {(item) => (
                      <Select.Item id={item.id}>{item.label}</Select.Item>
                    )}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        {form.watch('bilingual') === 'other' && (
          <FormField
            control={form.control}
            name="bilingualOther"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={t('otherSpecify')}
                    labelClassName={styles.inputLabel}
                    hideRequiredIndicator={false}
                    isRequired
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      <FormField
        control={form.control}
        name="additionalInformation"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <TextArea
                {...field}
                labelClassName={styles.inputLabel}
                label={t('addPartnerEvent.additionalInfoLabel')}
                rows={4}
                hint={`${MAX_ADDITIONAL_INFO_LENGTH - (field.value?.length || 0)} ${t('charactersLeft')}`}
                maxLength={MAX_ADDITIONAL_INFO_LENGTH}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {!hidePublicVisibility && (
        <div>
          <FormField
            control={form.control}
            name="isPublic"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Toggle
                    isSelected={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                    label={t('addPartnerEvent.publicVisibilityLabel')}
                    hint={t('addPartnerEvent.publicVisibilityHint')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      <FormField
        control={form.control}
        name="promoImageUrl"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              {renderBannerGallery ? (
                renderBannerGallery({
                  value: field.value ?? '',
                  onChange: (value) => field.onChange(value)
                })
              ) : (
                <div className={styles.bannerGalleryPlaceholder}>
                  Banner Gallery - to be provided by parent component
                </div>
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className={styles.externalUrlContainer}>
        <div className={styles.externalUrlHeader}>
          <PopOut className={styles.externalUrlIcon} />
          <h3 className={styles.sectionTitle}>
            {t('addPartnerEvent.externalRegistrationUrlLabel')}
          </h3>
        </div>
        <p className={styles.sectionDescription}>
          {t('addPartnerEvent.externalRegistrationUrlHint')}
        </p>
        <FormField
          control={form.control}
          name="externalRegistrationUrl"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  labelClassName={styles.inputLabel}
                  placeholder={t(
                    'addPartnerEvent.externalRegistrationUrlPlaceholder'
                  )}
                  type="text"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {!hideLiabilityWaiver && (
        <FormField
          control={form.control}
          name="isLiabilityAccepted"
          render={({ field }) => (
            <FormItem className={styles.checkboxItem}>
              <FormControl>
                <Checkbox
                  isSelected={field.value}
                  onChange={field.onChange}
                  label={
                    <span className={styles.liabilityWaiverLabel}>
                      {t('addPartnerEvent.termsCheckboxLabel.1')}{' '}
                      <a
                        href="https://www.goodneighbor.com/partner-liability-waiver"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.liabilityWaiverLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t(
                          'addPartnerEvent.termsCheckboxLabel.liabilityWaiver'
                        )}
                      </a>
                      {'.'}
                    </span>
                  }
                  className={styles.checkbox}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}
